import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Copy, Mail, MessageSquare, RefreshCw, Send, X } from 'lucide-react';
import styles from './VlSupportPanel.module.css';
import {
  bulkEventSnapshot,
  buildVlSupportBulkDetailsBlock,
  buildVlSupportBulkDraft,
  buildVlSupportDetailsBlock,
  buildVlSupportDraft,
  syncAccountLabel,
  type VlSupportSyncAccount,
  summarizeVlSupport,
  VL_SUPPORT_FROM_EMAIL,
  VL_SUPPORT_TEST_TO_EMAIL,
  VL_SUPPORT_TO_EMAIL,
  type VlSupportBulkDetails,
  type VlSupportEmailType,
  type VlSupportEventDetails,
} from './vlSupportEmail';
import {
  getVlSupportBarcodeSyncOptions,
  sendVlSupportEmail,
  VL_SUPPORT_MARKETPLACES,
  type VlSupportEventSyncOptions,
} from './api/vlListing.api';
import { MarketplaceBrandMark } from './EventMarketplaceDeepLinks';
import { OwnerPicker } from '../SupportTickets/components/OwnerPicker';
import { TEAM_OWNER } from '../SupportTickets/supportOwners';
import type { VlSupportRequest } from '../../types/ticket.types';

const MARKETPLACE_LOGO_IDS: Record<string, string> = {
  StubHub: 'stubhub',
  SeatGeek: 'seatgeek',
  Ticketmaster: 'ticketmaster',
  'Vivid Seats': 'vividseats',
  TickPick: 'tickpick',
  GameTime: 'gametime',
};

/** e.g. "October 22nd" */
const formatOrdinalDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.getDate();
  const suffix =
    day % 100 >= 11 && day % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] || 'th';
  return `${date.toLocaleDateString(undefined, { month: 'long' })} ${day}${suffix}`;
};

const OwnerTag = ({ initials }: { initials?: string | null }) =>
  initials ? <span className={styles.ownerTag} title={`Tagged to ${initials}`}>{initials}</span> : null;

type ControlProps = {
  tickets: Array<{ vl_support_requests?: VlSupportRequest[] | null }>;
  open: boolean;
  busy?: boolean;
  onToggle: () => void;
  onComplete: () => void;
  /** Opens the Victory Live email thread for this card's ticket. */
  onOpenThread?: (ticketId: string) => void;
  threadOpen?: boolean;
};

/** Button and status badge shown on a Listed event card. */
export function VlSupportControl({
  tickets,
  open,
  busy = false,
  onToggle,
  onComplete,
  onOpenThread,
  threadOpen = false,
}: ControlProps) {
  const summary = summarizeVlSupport(tickets);
  const stop = (event: React.MouseEvent) => event.stopPropagation();

  const mailButton = (
    <button
      type="button"
      className={`${styles.iconAction} ${open ? styles.iconActionActive : ''}`}
      onClick={onToggle}
      aria-expanded={open}
      aria-label="Contact Victory Live"
      title="Contact Victory Live"
    >
      <Mail size={14} />
    </button>
  );

  return (
    <div className={styles.control} onClick={stop}>
      {summary.state === 'contacted' && (
        <span className={styles.badgeContacted} title="Waiting on Victory Live">
          Open support ticket · {formatOrdinalDate(summary.sentAt)}
          <OwnerTag initials={summary.ownerInitials} />
        </span>
      )}
      {summary.state === 'completed' && (
        <span className={styles.badgeCompleted}>
          <CheckCircle2 size={11} /> VL support ticket completed
          <OwnerTag initials={summary.ownerInitials} />
        </span>
      )}
      <div className={styles.iconRow}>
        {summary.state !== 'contacted' && mailButton}
        {summary.state !== 'none' && summary.ticketId && onOpenThread && (
          <button
            type="button"
            className={`${styles.iconAction} ${threadOpen ? styles.iconActionActive : ''}`}
            onClick={() => onOpenThread(summary.ticketId as string)}
            aria-expanded={threadOpen}
            aria-label="View the Victory Live thread"
            title="View the Victory Live thread"
          >
            <MessageSquare size={14} />
            {summary.state === 'contacted' && summary.unreadCount > 0 && (
              <span className={styles.iconBadge}>{summary.unreadCount}</span>
            )}
          </button>
        )}
        {summary.state === 'contacted' && (
          <button
            type="button"
            className={`${styles.iconAction} ${styles.iconActionComplete}`}
            onClick={onComplete}
            disabled={busy}
            aria-label="Mark support ticket complete"
            title="Mark support ticket complete"
          >
            <CheckCircle2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

const sameAccount = (a: VlSupportSyncAccount, b: VlSupportSyncAccount) =>
  a.method === b.method && (a.team || null) === (b.team || null);

type PanelProps = {
  tpIds: string[];
  /** Single listing's details. Not used when `bulk` is given. */
  details?: VlSupportEventDetails | null;
  /** Several listings of one event or one user. The email lists events and seats only, never a user's name. */
  bulk?: VlSupportBulkDetails | null;
  onClose: () => void;
  onSent: (result: { sentTo?: string; redirectedForNonProd?: boolean; warning?: string }) => void;
  /** Initials this ticket will be tagged to, from the logged-in admin's name. */
  ownerInitials?: string;
  onError: (message: string) => void;
  /** False when this server has no vl_support_request table yet. */
  trackingAvailable?: boolean;
  /** Verification tab: only the Need PID email is offered. */
  lockedType?: VlSupportEmailType;
};

/** Email composer that expands under a Listed event row. */
export function VlSupportPanel({
  tpIds,
  details = null,
  bulk = null,
  onClose,
  onSent,
  onError,
  trackingAvailable = true,
  ownerInitials = '',
  lockedType,
}: PanelProps) {
  const [type, setType] = useState<VlSupportEmailType>(lockedType ?? 'not_populating');
  const [marketplaces, setMarketplaces] = useState<string[]>([]);
  // Barcode requests name the account we tried: the team's Season Seat Manager account, or Purchase Manager.
  const [syncEvents, setSyncEvents] = useState<VlSupportEventSyncOptions[] | null>(null);
  const [syncChoice, setSyncChoice] = useState<number[]>([]);
  const [syncError, setSyncError] = useState<string | null>(null);
  const chosenAccounts = (choice: number[] = syncChoice): VlSupportSyncAccount[] | null =>
    syncEvents ? syncEvents.map((e, i) => e.options[choice[i] ?? 0] ?? e.selected) : null;

  const makeDraft = (
    draftType: VlSupportEmailType,
    draftMarketplaces: string[],
    accounts: VlSupportSyncAccount[] | null = chosenAccounts(),
  ) =>
    bulk
      ? buildVlSupportBulkDraft(draftType, draftMarketplaces, bulk, accounts)
      : buildVlSupportDraft(draftType, draftMarketplaces, details as VlSupportEventDetails, accounts);
  const detailsBlock = bulk
    ? buildVlSupportBulkDetailsBlock(bulk)
    : buildVlSupportDetailsBlock(details as VlSupportEventDetails);
  const eventSnapshot = bulk ? bulkEventSnapshot(bulk, tpIds.length) : (details as VlSupportEventDetails);
  const initial = makeDraft(lockedType ?? 'not_populating', []);
  const [subject, setSubject] = useState(initial.subject);
  const [body, setBody] = useState(initial.body);
  const [dirty, setDirty] = useState(false);
  const [sending, setSending] = useState(false);
  // Who is in charge of this ticket. Team unless someone picks themselves or a teammate.
  const [owners, setOwners] = useState<string[]>([TEAM_OWNER]);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const subjectRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    bodyRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    let cancelled = false;
    getVlSupportBarcodeSyncOptions(tpIds)
      .then(({ events }) => {
        if (cancelled) return;
        setSyncEvents(events);
        setSyncChoice(events.map((e) => Math.max(0, e.options.findIndex((o) => sameAccount(o, e.selected)))));
      })
      .catch((error: any) => {
        if (!cancelled) setSyncError(error?.message || "Couldn't look up the sync account");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpIds.join(',')]);

  // Once the account is known, rewrite an untouched barcode email to name it.
  useEffect(() => {
    if (!syncEvents || type !== 'barcode_sync' || dirty) return;
    const draft = makeDraft('barcode_sync', marketplaces);
    setSubject(draft.subject);
    setBody(draft.body);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncEvents]);

  const chooseSyncAccount = (eventIndex: number, optionIndex: number) => {
    const next = syncChoice.map((v, i) => (i === eventIndex ? optionIndex : v));
    if (dirty && !window.confirm('This replaces the changes you made to the email. Continue?')) return;
    const draft = makeDraft(type, marketplaces, chosenAccounts(next));
    setSyncChoice(next);
    setSubject(draft.subject);
    setBody(draft.body);
    setDirty(false);
    setNote(null);
  };

  const regenerate = (nextType: VlSupportEmailType, nextMarketplaces: string[]) => {
    if (dirty && !window.confirm('This replaces the changes you made to the email. Continue?')) {
      return false;
    }
    const draft = makeDraft(nextType, nextMarketplaces);
    setSubject(draft.subject);
    setBody(draft.body);
    setDirty(false);
    setNote(null);
    if (nextType === 'other') window.setTimeout(() => subjectRef.current?.focus(), 0);
    return true;
  };

  const chooseType = (next: VlSupportEmailType) => {
    if (next === type) return;
    if (regenerate(next, marketplaces)) setType(next);
  };

  const toggleMarketplace = (name: string) => {
    const next = marketplaces.includes(name)
      ? marketplaces.filter((m) => m !== name)
      : VL_SUPPORT_MARKETPLACES.filter((m) => m === name || marketplaces.includes(m));
    if (regenerate(type, next)) setMarketplaces(next);
  };

  const copyToClipboard = async () => {
    const text = `To: ${VL_SUPPORT_TEST_TO_EMAIL || VL_SUPPORT_TO_EMAIL}\nSubject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      setNote({ kind: 'ok', text: 'Copied subject and email to your clipboard.' });
    } catch {
      setNote({ kind: 'err', text: "Couldn't copy. Select the text and copy it manually." });
    }
  };

  const send = async () => {
    if (type === 'not_populating' && !marketplaces.length) {
      setNote({ kind: 'err', text: 'Select at least one marketplace.' });
      return;
    }
    if (!subject.trim() || !body.trim()) {
      setNote({ kind: 'err', text: type === 'other' ? 'Add a subject and a message.' : "Subject and email can't be empty." });
      return;
    }
    if (type === 'other' && body.trim() === detailsBlock.trim()) {
      setNote({ kind: 'err', text: 'Type your message above the event details.' });
      return;
    }
    if (type === 'not_populating' && body.includes('[select a marketplace]')) {
      setNote({ kind: 'err', text: 'Replace "[select a marketplace]" before sending.' });
      return;
    }
    setSending(true);
    setNote(null);
    try {
      const result = await sendVlSupportEmail({
        tpIds,
        type,
        marketplaces: type === 'not_populating' ? marketplaces : [],
        subject: subject.trim(),
        body: body.trim(),
        owners,
        event: eventSnapshot,
        syncAccounts:
          type === 'barcode_sync' && syncEvents
            ? (chosenAccounts() || []).map((account, i) => ({ ...account, eventName: syncEvents[i].eventName }))
            : [],
      });
      onSent(result);
    } catch (error: any) {
      const message = error?.message || 'Failed to send email to Victory Live';
      setNote({ kind: 'err', text: message });
      onError(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.panel} onClick={(event) => event.stopPropagation()}>
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>
          <Mail size={14} /> Contact Victory Live
          {bulk && (
            <span className={styles.bulkTag}>
              {tpIds.length} tickets · {bulk.events.length} {bulk.events.length > 1 ? 'events' : 'event'}
            </span>
          )}
        </span>
        <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
          <X size={14} />
        </button>
      </div>

      {!trackingAvailable && (
        <div className={styles.noteWarn} role="note">
          Support tracking isn't set up on this server yet, so sending won't be recorded on the
          card. The database migration needs to be applied first.
        </div>
      )}

      {lockedType === 'need_pid' ? (
        <div className={styles.seg} role="group" aria-label="Request type">
          <button type="button" className={`${styles.segBtn} ${styles.segBtnOn}`}>
            Need PID
          </button>
        </div>
      ) : (
      <div className={styles.seg} role="group" aria-label="Request type">
        <button
          type="button"
          className={`${styles.segBtn} ${type === 'barcode_sync' ? styles.segBtnOn : ''}`}
          onClick={() => chooseType('barcode_sync')}
        >
          Help Syncing Barcodes
        </button>
        <button
          type="button"
          className={`${styles.segBtn} ${type === 'not_populating' ? styles.segBtnOn : ''}`}
          onClick={() => chooseType('not_populating')}
        >
          Not populating on a marketplace
        </button>
        <button
          type="button"
          className={`${styles.segBtn} ${type === 'other' ? styles.segBtnOn : ''}`}
          onClick={() => chooseType('other')}
        >
          Other
        </button>
      </div>
      )}

      {type === 'barcode_sync' && (
        <div className={styles.syncBox}>
          <div className={styles.syncTitle}>Account we tried to sync</div>
          {!syncEvents && !syncError && <div className={styles.hint}>Finding the team's sync account…</div>}
          {syncError && (
            <div className={styles.noteErr}>{syncError}. The email says all Purchase Manager accounts were tried.</div>
          )}
          {syncEvents?.map((event, i) => (
            <label key={`${event.eventName}-${i}`} className={styles.syncRow}>
              {syncEvents.length > 1 && <span className={styles.syncEvent}>{event.eventName}</span>}
              <select
                className={styles.syncSelect}
                value={syncChoice[i] ?? 0}
                onChange={(e) => chooseSyncAccount(i, Number(e.target.value))}
                aria-label={`Sync account for ${event.eventName}`}
              >
                {event.options.map((option, optionIndex) => (
                  <option key={optionIndex} value={optionIndex}>
                    {syncAccountLabel(option)}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      )}

      {type === 'not_populating' && (
        <div className={styles.chips} role="group" aria-label="Marketplaces">
          {VL_SUPPORT_MARKETPLACES.map((name) => (
            <button
              key={name}
              type="button"
              className={`${styles.chip} ${marketplaces.includes(name) ? styles.chipOn : ''}`}
              aria-pressed={marketplaces.includes(name)}
              onClick={() => toggleMarketplace(name)}
            >
              <MarketplaceBrandMark id={MARKETPLACE_LOGO_IDS[name] ?? ''} size={20} />
              {name}
            </button>
          ))}
        </div>
      )}

      <div className={styles.field}>
        <span className={styles.fieldLabel}>From</span>
        <span className={styles.fieldStatic}>{VL_SUPPORT_FROM_EMAIL}</span>
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>To</span>
        <span className={styles.fieldStatic}>
          {VL_SUPPORT_TEST_TO_EMAIL
            ? `${VL_SUPPORT_TEST_TO_EMAIL} (test inbox, not Victory Live)`
            : VL_SUPPORT_TO_EMAIL}
        </span>
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Subject</span>
        <input
          className={styles.input}
          value={subject}
          maxLength={300}
          ref={subjectRef}
          placeholder={type === 'other' ? 'Type a subject' : undefined}
          onChange={(event) => {
            setSubject(event.target.value);
            setDirty(true);
          }}
          aria-label="Email subject"
        />
      </div>
      <textarea
        ref={bodyRef}
        className={styles.textarea}
        rows={11}
        value={body}
        onChange={(event) => {
          setBody(event.target.value);
          setDirty(true);
        }}
        aria-label="Email body"
        placeholder={type === 'other' ? 'Type your message here. The event details are filled in below.' : undefined}
      />

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Tag to</span>
        <OwnerPicker value={owners} onChange={setOwners} myInitials={ownerInitials} disabled={sending} label="Who is in charge of this ticket" />
      </div>

      <div className={styles.footer}>
        <span className={styles.hint}>Replies go to {VL_SUPPORT_FROM_EMAIL}.</span>
        <span className={styles.spacer} />
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() => regenerate(type, marketplaces)}
          disabled={!dirty || sending}
        >
          <RefreshCw size={12} /> Reset text
        </button>
        <button type="button" className={styles.secondaryBtn} onClick={copyToClipboard} disabled={sending}>
          <Copy size={12} /> Copy
        </button>
        <button type="button" className={styles.primaryBtn} onClick={send} disabled={sending}>
          <Send size={12} /> {sending ? 'Sending…' : 'Send email'}
        </button>
      </div>
      {note && (
        <div className={note.kind === 'ok' ? styles.noteOk : styles.noteErr} role="status">
          {note.text}
        </div>
      )}
    </div>
  );
}
